import React, { useState, useEffect, useRef } from "react";
import {
  TableRow,
  TableCell,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Link,
} from "@mui/material";
import { format } from "date-fns";
import { PencilSimpleLine } from "@phosphor-icons/react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DueDate from "./DueDate";
import StatusPill from "./StatusPill";
import { formatNumberByDecimal } from "utils/formatNumberByDecimal";
import CompleteOffPlatformDialog from "components/CompleteOffPlatformDialog";
import VideoBreakdownDialog from "../SessionNotes";

const IncompleteOffPlatformRow = ({
  row,
  classes,
  navToClientDetails,
  userId,
  userPermissions,
  getIncompleteOffPlatform,
  getBillingItems,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  // Complete dialog state
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [offPlatformDetails, setOffPlatformDetails] = useState(null);

  // CPT code dialog state
  const [cptCodeDialogOpen, setCptCodeDialogOpen] = useState(false);
  const [cptCodeData, setCptCodeData] = useState(null);
  const [currentBreakdown, setCurrentBreakdown] = useState([]);

  // Track when both dialogs have closed to refresh incomplete off-platform items
  const wasAnyDialogOpen = useRef(false);
  useEffect(() => {
    const anyDialogOpen = completeDialogOpen || cptCodeDialogOpen;
    if (wasAnyDialogOpen.current && !anyDialogOpen) {
      if (typeof getIncompleteOffPlatform === "function") {
        getIncompleteOffPlatform();
        getBillingItems();
      }
    }
    wasAnyDialogOpen.current = anyDialogOpen;
  }, [completeDialogOpen, cptCodeDialogOpen, getIncompleteOffPlatform]);

  // Menu handlers
  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  // Complete dialog handlers
  const handleOpenCompleteDialog = () => {
    setOffPlatformDetails(row);
    setCompleteDialogOpen(true);
  };
  const handleCloseCompleteDialog = () => {
    setCompleteDialogOpen(false);
  };

  const handleOpenCptCodeDialog = (videoCallInfo) => {
    setCptCodeData(videoCallInfo);
    setCurrentBreakdown([
      {
        ...videoCallInfo,
        client_name: `${videoCallInfo.child_first_name} ${videoCallInfo.child_last_name}`,
        insurance_code: videoCallInfo.item_type,
        billable_time_id: videoCallInfo.id,
      },
    ]);
    setCptCodeDialogOpen(true);
    setCompleteDialogOpen(false);
  };
  const handleCloseCptCodeDialog = () => {
    setCptCodeDialogOpen(false);
    setCptCodeData(null);
  };

  return (
    <>
      <TableRow>
        <TableCell padding="checkbox">
          <div className={classes.tableCellCheckboxContainer}>
            <div style={{ width: 20 }} />
            {row.client_id ? (
              <Link onClick={() => navToClientDetails(row.client_id)}>
                {`${row.child_first_name} ${row.child_last_name}`}
              </Link>
            ) : (
              <Typography variant="body2">N/A</Typography>
            )}
          </div>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {row.start_date ? format(new Date(row.start_date), "P") : ""}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{row.service_type || "None Assigned"}</Typography>
        </TableCell>
        {userPermissions?.view_all_clients_billing_items && (
          <TableCell>
            <Typography variant="body2">{row.clinician_name}</Typography>
          </TableCell>
        )}
        <TableCell align="left">
          <Typography style={{ textAlign: "left" }} variant="body2">
            {formatNumberByDecimal(row.billable_time, 3)}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <StatusPill {...row} userPermissions={userPermissions} />
        </TableCell>
        <TableCell align="left">
          <DueDate {...row} classes={classes} />
        </TableCell>
        <TableCell align="center">
          <IconButton color="inherit" aria-label="Actions" onClick={handleOpenMenu} size="small">
            <MoreVertIcon />
          </IconButton>
        </TableCell>
      </TableRow>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu} elevation={1}>
        {userId === row.clinician_user_id && (
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              handleOpenCompleteDialog();
            }}
          >
            <ListItemIcon>
              <PencilSimpleLine />
            </ListItemIcon>
            Edit
          </MenuItem>
        )}
      </Menu>

      {completeDialogOpen && (
        <CompleteOffPlatformDialog
          callDetails={offPlatformDetails}
          onToggleCompleteOffPlatformDialog={handleCloseCompleteDialog}
          openCptCodeDialog={handleOpenCptCodeDialog}
        />
      )}

      {cptCodeDialogOpen && (
        <VideoBreakdownDialog
          open={cptCodeDialogOpen}
          startDate={cptCodeData?.start_date}
          endDate={cptCodeData?.end_date}
          videoCallId={cptCodeData?.id}
          clientUserId={cptCodeData?.user_id}
          clientData={{
            child_name: cptCodeData?.child_first_name,
            child_last_name: cptCodeData?.child_last_name,
          }}
          clinicianUserId={cptCodeData?.clinician_user_id}
          onCloseCPTCodeDialog={handleCloseCptCodeDialog}
          inVideoCall={false}
          isDemo={false}
          billingType={cptCodeData?.billing_type}
          sessionNoteId={cptCodeData?.sessionNoteId}
          billableTimeId={cptCodeData?.billableTimeId}
          clientId={cptCodeData?.client_id}
          enterInsuranceCode={true}
          showExtendedInfo
          videoBreakdown={[currentBreakdown]}
          isSecondary={false}
        />
      )}
    </>
  );
};

export default IncompleteOffPlatformRow;
